import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Clock, 
  RefreshCw, 
  ExternalLink,
  Calendar,
  Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDateVerificationPage() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningVerification, setRunningVerification] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchDashboard();
    fetchVerifications();
  }, [navigate, statusFilter]);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/date-verification/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setDashboard(data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  };

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const statusParam = statusFilter !== 'all' ? `&status=${statusFilter}` : '';
      const res = await fetch(`${API_URL}/api/admin/date-verification/list?limit=50${statusParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setVerifications(data.verifications);
      }
    } catch (err) {
      console.error('Error fetching verifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const runVerification = async (limit = 20) => {
    try {
      setRunningVerification(true);
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/date-verification/run-now?limit=${limit}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        alert(data.message);
        // Refresh after a delay
        setTimeout(() => {
          fetchDashboard();
          fetchVerifications();
        }, 5000);
      }
    } catch (err) {
      console.error('Error running verification:', err);
    } finally {
      setRunningVerification(false);
    }
  };

  const verifyEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/date-verification/verify-single/${eventId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        fetchVerifications();
      }
    } catch (err) {
      console.error('Error verifying event:', err);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'discrepancy':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'pending':
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'bg-green-100 text-green-800';
      case 'discrepancy':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'pending':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="date-verification-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Date Verification</h1>
            <p className="text-gray-600 mt-1">
              Automatically verify event dates against official sources
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => runVerification(10)}
              disabled={runningVerification}
              variant="outline"
              className="flex items-center gap-2"
              data-testid="run-verification-btn"
            >
              <RefreshCw className={`w-4 h-4 ${runningVerification ? 'animate-spin' : ''}`} />
              Verify 10 Events
            </Button>
            <Button
              onClick={() => runVerification(50)}
              disabled={runningVerification}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600"
              data-testid="run-batch-verification-btn"
            >
              <Search className="w-4 h-4" />
              Batch Verify (50)
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-gray-900">
                  {dashboard.stats.total_events}
                </div>
                <div className="text-sm text-gray-600">Total Events</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-green-600">
                  {dashboard.stats.by_status.verified}
                </div>
                <div className="text-sm text-gray-600">Verified</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-yellow-600">
                  {dashboard.stats.by_status.discrepancy}
                </div>
                <div className="text-sm text-gray-600">Discrepancies</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-gray-600">
                  {dashboard.stats.by_status.pending}
                </div>
                <div className="text-sm text-gray-600">Pending</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {dashboard.stats.coverage_percent}%
                </div>
                <div className="text-sm text-gray-600">Coverage</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recent Runs */}
        {dashboard?.recent_runs?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Verification Runs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {dashboard.recent_runs.map((run, idx) => (
                  <div 
                    key={idx} 
                    className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="text-sm text-gray-600">
                      {new Date(run.run_at).toLocaleString()}
                    </div>
                    <div className="flex gap-4 text-sm">
                      <span className="text-green-600">✓ {run.results.verified}</span>
                      <span className="text-yellow-600">⚠ {run.results.discrepancy}</span>
                      <span className="text-red-600">✗ {run.results.error}</span>
                      <span className="text-gray-600">Total: {run.results.total_checked}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 border-b border-gray-200 pb-2">
          {['all', 'verified', 'discrepancy', 'pending', 'error'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                statusFilter === status
                  ? 'bg-orange-100 text-orange-700 border-b-2 border-orange-500'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {/* Verifications List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : verifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No verifications found. Click "Batch Verify" to start checking events.
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {verifications.map((v, idx) => (
                  <div key={idx} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex items-start gap-3">
                        {getStatusIcon(v.status)}
                        <div>
                          <h3 className="font-semibold text-gray-900">{v.event_name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {v.stored_dates?.display || `${v.stored_dates?.start} - ${v.stored_dates?.end}`}
                            </span>
                          </div>
                          {v.discrepancy_details && (
                            <p className="text-sm text-gray-500 mt-1">
                              {v.discrepancy_details}
                            </p>
                          )}
                          {v.source_url && (
                            <a
                              href={v.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mt-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Official Website
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(v.status)}`}>
                          {v.status}
                        </span>
                        {v.reviewed && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            Reviewed
                          </span>
                        )}
                        <Button
                          onClick={() => verifyEvent(v.event_id)}
                          size="sm"
                          variant="outline"
                          className="text-xs"
                        >
                          Re-verify
                        </Button>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 mt-2">
                      Last checked: {new Date(v.verified_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
